import { useState } from "react";
import { Share2, MessageCircle, Facebook, Mail, Link2, Check } from "lucide-react";
import { toast } from "sonner";

// Telegram, WhatsApp and Facebook share endpoints — all without API keys
const SHARE_URL = "https://scudononni.it/";
const SHARE_TITLE = "ScudoNonni — Lo scudo dei nonni contro le truffe online";
const SHARE_TEXT =
  "Ho scoperto ScudoNonni: un sito gratuito che aiuta nonni e anziani a riconoscere truffe via SMS, email e WhatsApp. Bastano pochi secondi per controllare un messaggio sospetto. Passaparola, può salvare i risparmi a qualcuno che ami.";

function openShare(url) {
  window.open(url, "_blank", "noopener,noreferrer,width=600,height=520");
}

export default function ShareSection({ variant = "section" }) {
  const [copied, setCopied] = useState(false);

  const onWhatsApp = () => {
    const txt = encodeURIComponent(`${SHARE_TEXT}\n\n${SHARE_URL}`);
    openShare(`https://wa.me/?text=${txt}`);
  };

  const onTelegram = () => {
    const u = encodeURIComponent(SHARE_URL);
    const t = encodeURIComponent(`${SHARE_TITLE}\n\n${SHARE_TEXT}`);
    openShare(`https://t.me/share/url?url=${u}&text=${t}`);
  };

  const onFacebook = () => {
    const u = encodeURIComponent(SHARE_URL);
    openShare(`https://www.facebook.com/sharer/sharer.php?u=${u}`);
  };

  const onEmail = () => {
    const subject = encodeURIComponent(SHARE_TITLE);
    const body = encodeURIComponent(`${SHARE_TEXT}\n\n${SHARE_URL}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      toast.success("Link copiato! Ora puoi incollarlo dove vuoi.");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Non riesco a copiare. Prova a tenere premuto sul link.");
    }
  };

  const onNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: SHARE_TITLE,
          text: SHARE_TEXT,
          url: SHARE_URL,
        });
      } catch {
        // user cancelled — ignore
      }
    } else {
      onCopy();
    }
  };

  const buttons = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      onClick: onWhatsApp,
      Icon: MessageCircle,
      bg: "bg-[#25D366] hover:bg-[#1DAE52]",
    },
    {
      key: "telegram",
      label: "Telegram",
      onClick: onTelegram,
      Icon: Share2,
      bg: "bg-[#0088CC] hover:bg-[#006FA8]",
    },
    {
      key: "facebook",
      label: "Facebook",
      onClick: onFacebook,
      Icon: Facebook,
      bg: "bg-[#1877F2] hover:bg-[#1463CB]",
    },
    {
      key: "email",
      label: "Email",
      onClick: onEmail,
      Icon: Mail,
      bg: "bg-[#1E3A8A] hover:bg-[#152C69]",
    },
    {
      key: "copy",
      label: copied ? "Copiato!" : "Copia link",
      onClick: onCopy,
      Icon: copied ? Check : Link2,
      bg: copied ? "bg-[#16A34A] hover:bg-[#16A34A]" : "bg-gray-700 hover:bg-gray-800",
    },
  ];

  // Compact variant — small inline button bar
  if (variant === "compact") {
    return (
      <div
        data-testid="share-compact"
        className="bg-white border-4 border-blue-50 rounded-3xl p-5 md:p-6 shadow-md"
      >
        <p className="text-base md:text-lg font-semibold text-[#1E3A8A] mb-3 flex items-center gap-2">
          <Share2 size={20} /> Condividi ScudoNonni con chi ami
        </p>
        <div className="flex flex-wrap gap-2">
          {buttons.map((b) => (
            <button
              key={b.key}
              type="button"
              data-testid={`share-compact-${b.key}`}
              onClick={b.onClick}
              className={`${b.bg} text-white inline-flex items-center gap-2 rounded-2xl px-4 py-2.5 font-semibold text-base transition-all active:scale-95`}
            >
              <b.Icon size={18} />
              <span>{b.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Full section variant — for landing pages
  return (
    <section
      id="condividi"
      data-testid="share-section"
      className="bg-gradient-to-br from-[#DCFCE7] via-[#DBEAFE] to-white py-16 md:py-24"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-white text-[#16A34A] text-base font-bold mb-6 shadow-sm">
          <Share2 size={20} /> Passaparola
        </div>
        <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-[#1E3A8A] leading-tight max-w-3xl mx-auto">
          Aiutaci a proteggere altri nonni: <span className="text-[#22C55E]">condividi</span> ScudoNonni
        </h2>
        <p className="mt-5 text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
          Ogni truffa evitata vale risparmi salvati e serenità in più. Manda il sito
          a un nonno, a un genitore, a un amico — e diffondi la sicurezza online.
        </p>

        <div className="mt-10 grid grid-cols-2 sm:flex sm:flex-wrap sm:justify-center gap-3 md:gap-4 max-w-3xl mx-auto">
          {buttons.map((b) => (
            <button
              key={b.key}
              type="button"
              data-testid={`share-${b.key}`}
              onClick={b.onClick}
              className={`${b.bg} text-white flex items-center justify-center gap-3 rounded-2xl px-5 md:px-6 py-4 font-bold text-lg shadow-lg hover:shadow-xl transition-all active:scale-95 min-h-[64px]`}
            >
              <b.Icon size={26} />
              <span>{b.label}</span>
            </button>
          ))}
        </div>

        {/* Mobile-native share button */}
        {typeof navigator !== "undefined" && "share" in navigator && (
          <button
            type="button"
            data-testid="share-native"
            onClick={onNativeShare}
            className="mt-6 inline-flex items-center gap-3 bg-white border-4 border-[#1E3A8A] text-[#1E3A8A] hover:bg-blue-50 rounded-2xl px-6 py-3 font-bold text-lg shadow-md hover:shadow-lg transition-all active:scale-95"
          >
            <Share2 size={22} /> Altre app del telefono
          </button>
        )}

        <p className="mt-10 text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
          💡 Suggerimento: incolla il link in una <strong>chat di famiglia</strong> WhatsApp.
          Una persona informata può evitare a tutti i parenti di cadere in una truffa.
        </p>
      </div>
    </section>
  );
}
